/**
 * Currency utilities for normalizing and formatting currency codes
 */

/**
 * Map of common currency symbols and names to ISO 4217 currency codes
 */
const CURRENCY_CODE_MAP: Record<string, string> = {
  // Euro
  '\u20AC': 'EUR', // € symbol
  'EUR': 'EUR',
  'Euro': 'EUR',
  'euro': 'EUR',
  'EURO': 'EUR',

  // US Dollar
  '\u0024': 'USD', // $ symbol
  'USD': 'USD',
  'Dollar': 'USD',
  'dollar': 'USD',
  'US Dollar': 'USD',
  'US dollar': 'USD',

  // British Pound
  '\u00A3': 'GBP', // £ symbol
  'GBP': 'GBP',
  'Pound': 'GBP',
  'pound': 'GBP',
  'Sterling': 'GBP',

  // Japanese Yen
  '\u00A5': 'JPY', // ¥ symbol
  'JPY': 'JPY',
  'Yen': 'JPY',
  'yen': 'JPY',

  // Swiss Franc
  'CHF': 'CHF',
  'Franc': 'CHF',
  'Swiss Franc': 'CHF',

  // Chinese Yuan
  'CNY': 'CNY',
  'Yuan': 'CNY',
  'RMB': 'CNY',

  // Canadian Dollar
  'CAD': 'CAD',
  'Canadian Dollar': 'CAD',

  // Australian Dollar
  'AUD': 'AUD',
  'Australian Dollar': 'AUD',

  // Swedish Krona
  'SEK': 'SEK',
  'Krona': 'SEK',
  'Swedish Krona': 'SEK',

  // Norwegian Krone
  'NOK': 'NOK',
  'Krone': 'NOK',
  'Norwegian Krone': 'NOK',

  // Danish Krone
  'DKK': 'DKK',
  'Danish Krone': 'DKK',

  // Polish Złoty
  'PLN': 'PLN',
  'Zloty': 'PLN',
  'Polish Zloty': 'PLN',
};

/**
 * Normalize a currency code/symbol/name to a valid ISO 4217 currency code
 * @param currency - The currency string to normalize
 * @returns Valid ISO 4217 currency code or null if not recognized
 */
export function normalizeCurrencyCode(currency: string | undefined): string | null {
  if (!currency) {
    return null;
  }

  // Trim whitespace
  const trimmed = currency.trim();

  // Check if it's in the map
  if (CURRENCY_CODE_MAP[trimmed]) {
    return CURRENCY_CODE_MAP[trimmed];
  }

  // Check case-insensitive match
  const upperCurrency = trimmed.toUpperCase();
  if (CURRENCY_CODE_MAP[upperCurrency]) {
    return CURRENCY_CODE_MAP[upperCurrency];
  }

  // If it's already a 3-letter code (likely ISO 4217), return it
  if (/^[A-Z]{3}$/i.test(trimmed)) {
    return trimmed.toUpperCase();
  }

  // Unknown currency
  return null;
}

/**
 * Format a currency amount with proper formatting
 * @param amount - The numeric amount to format
 * @param currency - The currency code/symbol/name
 * @param locale - The locale to use for formatting (default: 'en-US')
 * @param options - Additional Intl.NumberFormat options
 * @returns Formatted currency string or empty string if invalid
 */
export function formatCurrencyAmount(
  amount: number | undefined,
  currency: string | undefined,
  locale: string = 'en-US',
  options: Partial<Intl.NumberFormatOptions> = {}
): string {
  if (amount === undefined || amount === null) {
    return '';
  }

  const normalizedCurrency = normalizeCurrencyCode(currency);

  if (!normalizedCurrency) {
    // Fallback: format as plain number if currency is invalid
    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
      ...options
    }).format(amount);
  }

  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: normalizedCurrency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
      ...options
    }).format(amount);
  } catch (error) {
    // If formatting fails, return plain number
    console.warn(`Failed to format currency ${normalizedCurrency}:`, error);
    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
      ...options
    }).format(amount);
  }
}
